import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Building2, UserCheck, BarChart3, Settings, LogOut,
  FileText, ChevronRight, Key, Brain, Activity, Mic, Calendar, MessageSquare
} from '../components/icons';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { RecentActivityFeed } from '../components/admin/RecentActivityFeed';
import { PartnerOverviewTable } from '../components/admin/PartnerOverviewTable';
import { InactiveCoachesAdmin } from '../components/admin/InactiveCoachesAdmin';
import { AdminAssessmentsChart } from '../components/admin/AdminAssessmentsChart';
import { AdminCodeRedemption } from '../components/admin/AdminCodeRedemption';
import { CoachTierChart } from '../components/admin/CoachTierChart';
import {
  fetchAdminStats,
  fetchAdminCodeRedemption,
  fetchAssessmentsOverTimeAdmin,
  fetchPartnerOverviews,
  fetchCoachTierBreakdown,
  fetchRecentActivity,
  fetchInactiveCoachesAdmin,
  type AdminStats,
  type AdminCodeRedemption as AdminCodeRedemptionData,
  type MonthlyAssessmentAdmin,
  type PartnerOverview,
  type CoachTierBreakdown,
  type RecentActivity,
  type InactiveCoachAdmin,
} from '../lib/admin-dashboard';

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats>({
    totalPartners: 0,
    totalCoaches: 0,
    totalRespondents: 0,
    totalAssessments: 0,
    totalCodes: 0,
    codesUsed: 0,
    codesAvailable: 0,
    aiAnalysisCount: 0,
  });
  const [codeRedemption, setCodeRedemption] = useState<AdminCodeRedemptionData>({ totalCodes: 0, usedCodes: 0, rate: 0 });
  const [assessmentsOverTime, setAssessmentsOverTime] = useState<MonthlyAssessmentAdmin[]>([]);
  const [partnerOverviews, setPartnerOverviews] = useState<PartnerOverview[]>([]);
  const [coachTiers, setCoachTiers] = useState<CoachTierBreakdown[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [inactiveCoaches, setInactiveCoaches] = useState<InactiveCoachAdmin[]>([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkAdminAndLoad();
  }, [user, navigate]);

  const checkAdminAndLoad = async () => {
    if (!user?.id) return;

    try {
      const isAdmin = await isUserAdmin(user.id);
      if (!isAdmin) {
        navigate('/login');
        return;
      }

      const [
        statsData,
        codeData,
        assessmentsData,
        partnersData,
        tiersData,
        activityData,
        inactiveData,
      ] = await Promise.all([
        fetchAdminStats(),
        fetchAdminCodeRedemption(),
        fetchAssessmentsOverTimeAdmin(),
        fetchPartnerOverviews(),
        fetchCoachTierBreakdown(),
        fetchRecentActivity(),
        fetchInactiveCoachesAdmin(),
      ]);

      setStats(statsData);
      setCodeRedemption(codeData);
      setAssessmentsOverTime(assessmentsData);
      setPartnerOverviews(partnersData);
      setCoachTiers(tiersData);
      setRecentActivity(activityData);
      setInactiveCoaches(inactiveData);
    } catch (err) {
      console.error('Error loading admin data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading admin dashboard..." />;
  }

  const statCards = [
    { label: 'Partners', value: stats.totalPartners, icon: Building2, bgColor: 'bg-brand-10', iconColor: 'text-brand-40' },
    { label: 'Coaches', value: stats.totalCoaches, icon: UserCheck, bgColor: 'bg-success-10', iconColor: 'text-success-40' },
    { label: 'Respondents', value: stats.totalRespondents, icon: Users, bgColor: 'bg-orange-10', iconColor: 'text-orange-40' },
    { label: 'Assessments', value: stats.totalAssessments, icon: FileText, bgColor: 'bg-teal-10', iconColor: 'text-teal-50' },
    { label: 'Codes Available', value: stats.codesAvailable, icon: Key, bgColor: 'bg-brand-10', iconColor: 'text-brand-60' },
    { label: 'AI Analyses', value: stats.aiAnalysisCount, icon: Brain, bgColor: 'bg-success-5', iconColor: 'text-success-50' },
  ];

  const menuItems = [
    { label: 'Partner Management', icon: Building2, path: '/admin/partners', description: 'Manage partner organizations' },
    { label: 'Coach Management', icon: UserCheck, path: '/admin/coaches', description: 'Manage coaches and their tiers' },
    { label: 'Free Profile Coaches', icon: UserCheck, path: '/admin/free-profile-coaches', description: 'View coaches who signed up via free profile link' },
    { label: 'Assessment Codes', icon: FileText, path: '/admin/codes', description: 'Generate and manage codes' },
    { label: 'Analytics', icon: BarChart3, path: '/admin/analytics', description: 'View detailed platform analytics' },
    { label: 'Audio Transcriber', icon: Mic, path: '/admin/transcriber', description: 'Transcribe audio to text' },
    { label: 'Event Registrations', icon: Calendar, path: '/admin/event-registrations', description: 'View Day 2 signups' },
    { label: 'Partner Applications', icon: Activity, path: '/admin/partner-applications', description: 'View pricing page applications' },
    { label: 'Live Chat', icon: MessageSquare, path: '/admin/live-chat', description: 'View audience messages' },
  ];

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-brand-90 rounded-xl shadow-soft">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-heading-xs text-gray-80">Admin Dashboard</h1>
              <p className="text-text-sm text-gray-40">Platform Management</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2.5 text-gray-60 hover:bg-gray-10 rounded-xl transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
              className="freud-card-elevated p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2.5 ${stat.bgColor} rounded-xl`}>
                  <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
              </div>
              <div className="text-heading-sm text-gray-80 mb-0.5">{stat.value}</div>
              <div className="text-text-xs text-gray-40">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {inactiveCoaches.length > 0 && (
          <div className="mb-8">
            <InactiveCoachesAdmin coaches={inactiveCoaches} />
          </div>
        )}

        <div className="freud-card-elevated p-6 mb-8">
          <h2 className="text-text-xl text-gray-80 mb-5">Quick Actions</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-4">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.08 }}
              >
                <Link
                  to={item.path}
                  className="block p-5 rounded-xl bg-brand-5 border border-gray-20 hover:bg-success-5 hover:border-success-10 transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-10 rounded-lg group-hover:bg-success-10 transition-colors">
                        <item.icon className="w-5 h-5 text-gray-50 group-hover:text-success-50 transition-colors" />
                      </div>
                      <div>
                        <h3 className="text-text-sm text-gray-80 font-semibold group-hover:text-success-50 transition-colors">{item.label}</h3>
                        <p className="text-text-xs text-gray-40">{item.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-30 group-hover:text-success-40 transition-colors" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <AdminAssessmentsChart data={assessmentsOverTime} />
          <AdminCodeRedemption data={codeRedemption} />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <CoachTierChart tiers={coachTiers} totalCoaches={stats.totalCoaches} />
          <RecentActivityFeed activities={recentActivity} />
        </div>

        <div className="mb-8">
          <PartnerOverviewTable partners={partnerOverviews} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-r from-brand-90 to-brand-60 rounded-2xl p-8 text-white shadow-soft-lg"
        >
          <div className="flex items-center gap-4 mb-4">
            <Activity className="w-8 h-8" />
            <h3 className="text-text-xl font-bold text-white">Platform Status</h3>
          </div>
          <p className="text-brand-10">
            All systems operational. {stats.totalAssessments} assessments completed across {stats.totalPartners} partner{stats.totalPartners !== 1 ? 's' : ''} with {stats.totalCoaches} active coach{stats.totalCoaches !== 1 ? 'es' : ''}.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
